import { useMutation } from "api";

/**
 * DEMO ONLY — partner marketing consent with no backend behind it.
 *
 * The real design has the API decide which orders carry a partner prompt, send
 * the exact copy it will store alongside the consent record, and persist the
 * answer server-side. None of that exists yet, so this module stands in for all
 * three: the partner is matched off the product vendor already on the order, the
 * copy lives here, and the answer goes to localStorage.
 *
 * That means a saved preference is per-browser and carries no legal weight —
 * it is here so the flow can be clicked through, nothing more. When the API
 * lands, delete this file, read `consent_prompt` off the order payload, and
 * point `useSubmitConsent` at `POST /collectors/orders/{id}/consent`.
 */

const STORAGE_PREFIX = "collectors:consent";

// Keyed by lowercased Shopify product vendor. `line_items[].artist` on the
// certificate payload *is* the vendor (see `_get_product_image`'s sibling in
// collectors.py: `artist = product.get("vendor")`), so no new API field is
// needed to match one.
const PARTNERS = {
  cadillac: {
    partner: "cadillac",
    display_name: "Cadillac",
    version: "cadillac-consent-2026-09-demo",
    intro:
      "Cadillac is the brand partner behind the artwork in this order. If you'd like to hear from them, you can say so below. Both options are optional and your certificates are yours either way.",
    marketing_statement:
      "Email me about Cadillac news, events and future artwork releases. I can unsubscribe at any time, as described in the {privacy_policy}.",
    sharing_statement:
      "Share my name and email address with Cadillac so they can contact me directly, handled under the {privacy_policy}.",
    privacy_policy_placeholder: "{privacy_policy}",
    privacy_policy_label: "Cadillac privacy policy",
    privacy_policy_url: "https://www.cadillac.com/privacy",
    withdrawal_note:
      "You can change or withdraw either preference at any time — come back to this page, or email orders@oditi.com. Withdrawing won't affect the artwork or certificates in this order.",
  },
};

function storageKey(orderId, partner) {
  return `${STORAGE_PREFIX}:${orderId}:${partner}`;
}

/** localStorage throws outright in some privacy modes, so every touch is guarded. */
function readSaved(orderId, partner) {
  try {
    const raw = window.localStorage.getItem(storageKey(orderId, partner));
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    return {
      marketing_consent: Boolean(parsed.marketing_consent),
      partner_sharing_consent: Boolean(parsed.partner_sharing_consent),
      captured_at: parsed.captured_at || null,
    };
  } catch (error) {
    return null;
  }
}

function writeSaved(orderId, partner, record) {
  try {
    window.localStorage.setItem(storageKey(orderId, partner), JSON.stringify(record));
  } catch (error) {
    // A demo that can't remember is still a demo that works.
  }
}

/**
 * The consent prompt for an order, or null if no line item comes from a partner
 * we have copy for. Stands in for the API's `consent_prompt` field.
 *
 * `bound_to` is deliberately absent: the certificate payload carries no customer
 * email, and inventing one on a consent screen would be worse than omitting the
 * line (ConsentCard already guards it).
 */
export function consentPromptForOrder(data, orderId) {
  // DEMO escape hatch: `?consent=cadillac` forces the card onto any order, so the
  // flow can be shown without hunting for one that has the partner's work on it.
  // An unrecognised value falls through to the vendor match rather than
  // suppressing the card, so a typo in the query string can't hide real work.
  const forced = new URLSearchParams(window.location.search).get("consent");
  const partner = (forced && PARTNERS[forced.trim().toLowerCase()]) || matchByVendor(data);

  if (!partner) return null;

  return { ...partner, current: readSaved(orderId, partner.partner) };
}

/**
 * `includes` rather than an exact match: the vendor on a real order is as likely
 * to read "Cadillac Europe" as "Cadillac", and a demo that misses on a trailing
 * word is worse than one that occasionally matches too eagerly.
 */
function matchByVendor(data) {
  const lineItems = data?.["line_items"] || [];

  for (const item of lineItems) {
    const vendor = String(item?.artist || "").toLowerCase();
    const key = Object.keys(PARTNERS).find((name) => vendor.includes(name));
    if (key) return PARTNERS[key];
  }

  return null;
}

/**
 * Mirrors the shape the real endpoint would return — the saved record, echoed
 * back with a server-assigned timestamp — so ConsentCard needs no demo-specific
 * branches and keeps working once this is swapped for a fetch.
 */
export const useSubmitConsent = () =>
  useMutation({
    mutationFn: async ({ orderId, partner, marketingConsent, partnerSharingConsent }) => {
      // Latency so the pending state is actually visible when demoing.
      await new Promise((resolve) => setTimeout(resolve, 600));

      const record = {
        marketing_consent: Boolean(marketingConsent),
        partner_sharing_consent: Boolean(partnerSharingConsent),
        captured_at: new Date().toISOString(),
      };
      writeSaved(orderId, partner, record);

      return record;
    },
  });
